import React from "react";
import { cn } from "@/lib/utils";
import Eyebrow from "./Eyebrow";
import Reveal from "./Reveal";

/**
 * SectionHeader — eyebrow + heading + lead.
 * Use at the top of every content section for consistent typography rhythm.
 *
 * Props:
 *   eyebrow      — small tag text (optional)
 *   eyebrowIcon  — Lucide icon for the eyebrow (optional)
 *   title        — string OR ReactNode (use <span className="gradient-text"> inside)
 *   description  — body lede text
 *   align        — "left" | "center" (default: center)
 *   maxWidth     — Tailwind max-w class for description (default: max-w-2xl)
 */
const SectionHeader = ({
  eyebrow,
  eyebrowIcon,
  title,
  description,
  align = "center",
  maxWidth = "max-w-2xl",
  className = "",
  children,
}) => {
  const isCenter = align === "center";
  return (
    <div
      className={cn(
        "mb-10 md:mb-14",
        isCenter ? "text-center mx-auto" : "text-left",
        className,
      )}
    >
      {eyebrow && (
        <Reveal variant="up" duration={0.5}>
          <div className={cn("mb-4", isCenter ? "flex justify-center" : "")}>
            <Eyebrow icon={eyebrowIcon}>{eyebrow}</Eyebrow>
          </div>
        </Reveal>
      )}

      {title && (
        <Reveal variant="up" delay={0.05} duration={0.7}>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            {title}
          </h2>
        </Reveal>
      )}

      {description && (
        <Reveal variant="up" delay={0.1} duration={0.7}>
          <p
            className={cn(
              "mt-4 text-base sm:text-lg text-[var(--text-secondary)] leading-relaxed",
              maxWidth,
              isCenter ? "mx-auto" : "",
            )}
          >
            {description}
          </p>
        </Reveal>
      )}

      {children && (
        <Reveal variant="up" delay={0.15} duration={0.7}>
          <div className={cn("mt-6", isCenter ? "flex justify-center" : "")}>{children}</div>
        </Reveal>
      )}
    </div>
  );
};

export default SectionHeader;
